import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import BackendInfiniteScroll from "../../../../components/BackendInfiniteScroll/BackendInfiniteScroll";
import { Button } from "../../../../components/Button";
import Dropdown from "../../../../components/Dropdowns/Dropdown";
import { Input } from "../../../../components/Input";
import Loading from "../../../../components/Loading/Loading";
import ModalVariacoesPromocao from "../../../../components/ModalVariacoesPromocao/ModalVariacoesPromocao";
import Produto from "../../../../components/Produto";
import { Select } from "../../../../components/Select";
import SkeletonProdutos from "../../../../components/Skeletons/SkeletonProdutos/SkeletonProdutos";
import { CatalogoContext } from "../../../../contexts/Catalogo/CatalogoContext";
import { useTheme } from "../../../../contexts/Theme/ThemeContext";
import { ProdutosContext } from "../../../../contexts/Produtos/ProdutosContext";
import { produtosApi, GetProdutosPaginadoParams } from "../../../../hooks/produtosApi";
import { boolean } from "../../../../services/Functions";
import { Produto as ProdutoType } from "../../../../types/Produto";
import ProdutosPromocaoContainer from "./styles";

interface ProdutosPromocaoProps {
  produtosCatalogo: number[];
  produtosPromocaoAtual: number[];
  produtosEmOutraPromocao: Set<number>;
  variacoesEmOutraPromocao: { [id_produto: number]: number[] };
  variacoesPorProduto: { [id_produto: number]: number[] };
  onAdicionar: (id: number) => void;
  onRemover: (id: number) => void;
  onAdicionarTodos: (produtosFiltrados: number[]) => void;
  onRemoverTodos: () => void;
  onAlterarVariacoes: (id_produto: number, variacoes: number[]) => void;
  editandoIndex?: number | null;
}

const ProdutosPromocao: React.FC<ProdutosPromocaoProps> = ({
  produtosCatalogo,
  produtosPromocaoAtual,
  produtosEmOutraPromocao,
  variacoesEmOutraPromocao,
  variacoesPorProduto,
  onAdicionar,
  onRemover,
  onAdicionarTodos,
  onRemoverTodos,
  onAlterarVariacoes,
  editandoIndex,
}) => {
  const {
    loading,
    setLoading,
    produtos,
    categorias,
    init,
    filtros,
    setFiltros,
    filtroPesquisaAux,
    setFiltroPesquisaAux,
    hasMore,
    fetchMore,
    loadingMore,
  } = useContext(ProdutosContext);

  const { theme } = useTheme();
  const context = useContext(CatalogoContext);
  const catalogo = context ? context.catalogo : null;
  const listasDePreco = context ? context.listasDePrecos : null;
  const ajustesVariacao = context ? context.ajustesVariacao : [];

  // Criar mapa produto -> ajuste
  const produtoAjusteMap = useMemo(() => {
    const map = new Map<number, { tipo: string; direcao: string; valor: number }>();
    ajustesVariacao?.forEach(ajuste => {
      if (ajuste.ativo) {
        ajuste.produtos.forEach(produtoId => {
          map.set(produtoId, {
            tipo: ajuste.ajuste_tipo,
            direcao: ajuste.ajuste_direcao,
            valor: Number(ajuste.ajuste_valor),
          });
        });
      }
    });
    return map;
  }, [ajustesVariacao]);

  const semVariacaoLivre = useCallback(
    (id_produto: number, totalVariacoes: number) => {
      if (produtosEmOutraPromocao.has(id_produto)) return true;
      const bloqueadas = variacoesEmOutraPromocao[id_produto];
      if (!bloqueadas || !bloqueadas.length || !totalVariacoes) return false;
      return bloqueadas.length >= totalVariacoes;
    },
    [produtosEmOutraPromocao, variacoesEmOutraPromocao]
  );

  const [filtroAplicado, setFiltroAplicado] = useState(false);
  const [loadingTodos, setLoadingTodos] = useState(false);
  const [produtoVariacoes, setProdutoVariacoes] = useState<ProdutoType | null>(null);
  const produtosScroll = useRef<HTMLDivElement>(null);

  let moeda = "R$";
  if (context) {
    let listaCatalogo = listasDePreco?.filter(
      (lista) => lista.id === catalogo?.id_tabela
    );
    if (listaCatalogo?.length) moeda = listaCatalogo[0].moeda;
  }

  // Se "adicionarTodosProdutos" está ativo, mostra todos os produtos
  // Caso contrário, mostra apenas os produtos do catálogo
  const produtosDisponiveis = useMemo(() => {
    if (catalogo?.configuracoes?.adicionarTodosProdutos) {
      return produtos;
    }
    return produtos.filter((p) => produtosCatalogo.includes(p.id));
  }, [produtos, produtosCatalogo, catalogo?.configuracoes?.adicionarTodosProdutos]);

  // Produtos filtrados (filtros agora são aplicados no backend)
  const produtosFiltrados = produtosDisponiveis;

  // Não inicializa automaticamente — espera o usuário clicar em "Aplicar filtros"

  const optionsVisualizacao = [
    { value: "todos", name: "Todos os produtos" },
    { value: "com_promocao", name: "Com promoção" },
    { value: "sem_promocao", name: "Sem promoção" },
  ];

  const duration = 250;
  const handleAplicarFiltro = () => {
    init();
    setFiltroAplicado(true);
    setTimeout(() => {
      if (produtosScroll.current) {
        window.scrollTo({
          top:
            window.scrollY +
            produtosScroll.current.getBoundingClientRect().top -
            100,
          behavior: "smooth",
        });
      }
    }, duration);
  };

  useEffect(() => {
    if (editandoIndex !== null && editandoIndex !== undefined) {
      if (filtros.filtroPromocao !== 'com_promocao') {
        setFiltros({ ...filtros, filtroPromocao: 'com_promocao' });
      }
      if (!filtroAplicado) {
        handleAplicarFiltro();
      }
    }
  }, [editandoIndex]);

  const adicionarRemoverTodos = useCallback(
    async (adicionar: boolean) => {
      setLoadingTodos(true);
      try {
        const params: GetProdutosPaginadoParams = {
          search: filtros.filtroPesquisa,
          categoria: filtros.filtroCategoria,
          ativos: "1",
        };

        if (catalogo) {
          params.id_tabela = catalogo.id_tabela;
          params.id_catalogo = catalogo.id;
          params.promocao = filtros.filtroPromocao;
          if (produtosPromocaoAtual.length) {
            params.ids_promocao = produtosPromocaoAtual;
          }
          if (!boolean(catalogo.configuracoes.adicionarTodosProdutos)) {
            params.ids_catalogo = catalogo.produtos.length ? catalogo.produtos : [0];
          }
        }

        const ids = await produtosApi().getIdsFiltrados(params);

        if (adicionar) {
          const totalPorProduto = new Map(
            produtos.map(p => [p.id, p.qtd_variacoes ?? 0])
          );
          const idsParaAdicionar = ids.filter(
            id => !semVariacaoLivre(id, totalPorProduto.get(id) ?? 0)
          );
          onAdicionarTodos(idsParaAdicionar);
        } else {
          onRemoverTodos();
        }
      } catch (error) {
        Swal.fire({
          title: "Erro",
          text: "Não foi possível carregar todos os produtos. Tente novamente.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        setLoadingTodos(false);
      }
    },
    [filtros, catalogo, produtos, semVariacaoLivre, onAdicionarTodos, onRemoverTodos]
  );

  return (
    <ProdutosPromocaoContainer theme={theme}>
      <div className="descricao fonteRotulos">
        Nessa listagem, aparecem apenas os produtos que já foram adicionados ao
        catálogo. Selecione quais produtos devem receber essa promoção.
      </div>

      <div className="grid-header">
        <div className="input full-width">
          <Input
            label="Pesquisar"
            type={"text"}
            icone="icons/search.svg"
            placeholder={"Por SKU ou descrição"}
            value={filtroPesquisaAux}
            onChange={(newValue) => {
              newValue === filtros.filtroPesquisa
                ? setLoading(false)
                : setLoading(true);
              setFiltroPesquisaAux(newValue);
            }}
            onDebounce={(newValue) =>
              setFiltros({ ...filtros, filtroPesquisa: newValue })
            }
            debounceTime={500}
          />
        </div>
        <div className="select half-width">
          <Select
            sizeLabel="medium"
            label="Filtrar por categoria"
            options={[
              { value: "0", name: "Todas as categorias" },
              ...categorias,
            ]}
            value={filtros.filtroCategoria}
            onChange={(newValue) => {
              setFiltros({ ...filtros, filtroCategoria: newValue });
            }}
          />
        </div>
        <div className="select half-width">
          <Select
            sizeLabel="medium"
            label="Visualização"
            options={optionsVisualizacao}
            value={filtros.filtroPromocao || "todos"}
            onChange={(newValue) => {
              setFiltros({ ...filtros, filtroPromocao: newValue as 'todos' | 'com_promocao' | 'sem_promocao' });
            }}
          />
        </div>
      </div>

      <div>
        {!filtroAplicado && (
          <Button
            title="Aplicar filtros"
            className="button-filtrar fonteTexto"
            width="100%"
            onClick={() => handleAplicarFiltro()}
          />
        )}
      </div>

      <Dropdown active={filtroAplicado} animated={true} duration={duration}>
        <div className="produtos-wrapper">
          <div className="informacoes">
            <div className="flex-container-produtos btn-adicionar-remover">
              <div className="container-half">
                <Button
                  width="100%"
                  disabled={loadingTodos}
                  loading={loadingTodos}
                  className="botao fonteTexto"
                  title="Adicionar Todos"
                  onClick={() => adicionarRemoverTodos(true)}
                />
              </div>
              <div className="container-half">
                <Button
                  width="100%"
                  disabled={loadingTodos}
                  loading={loadingTodos}
                  className="botao cinza fonteTexto"
                  title="Remover Todos"
                  onClick={() => adicionarRemoverTodos(false)}
                />
              </div>
            </div>
          </div>

          <div ref={produtosScroll} className="produtos scrollBonito">
            {loading ? (
              <SkeletonProdutos />
            ) : (
              <BackendInfiniteScroll
                next={fetchMore}
                hasMore={hasMore}
                loader={<Loading relative />}
              >
                <>
                  {produtosFiltrados.map((produto: ProdutoType) => {
                    const naPromocao = produtosPromocaoAtual.includes(produto.id);
                    const ajuste = produtoAjusteMap.get(produto.id);

                    const totalVariacoes = produto.qtd_variacoes ?? 0;
                    const bloqueadas = variacoesEmOutraPromocao[produto.id] || [];
                    const emOutra = semVariacaoLivre(produto.id, totalVariacoes);
                    const disponiveis = totalVariacoes - bloqueadas.length;
                    const variacoesEscolhidas = variacoesPorProduto[produto.id] || [];
                    const todasVariacoes =
                      variacoesEscolhidas.length === 0 ||
                      (bloqueadas.length > 0 &&
                        variacoesEscolhidas.length >= disponiveis);

                    return (
                      <div
                        className={`produto-container ${
                          emOutra ? "em-outra-promocao" : ""
                        }`}
                        key={produto.id}
                      >
                        <Produto
                          produto={{ ...produto, ajuste }}
                          moeda={moeda}
                          openImage={false}
                        >
                          <div className="button-wrapper">
                            {totalVariacoes > 1 && !emOutra && (
                              <div
                                className="seletor-variacoes checkboxBonito"
                                onClick={() => setProdutoVariacoes(produto)}
                              >
                                <input
                                  type="checkbox"
                                  checked={todasVariacoes}
                                  readOnly
                                />
                                <span className="texto fonteRotulos">
                                  {bloqueadas.length
                                    ? todasVariacoes
                                      ? `Todas as ${disponiveis} disponíveis`
                                      : `${variacoesEscolhidas.length} de ${disponiveis} disponíveis`
                                    : todasVariacoes
                                    ? "Todas as variações"
                                    : `${variacoesEscolhidas.length} de ${totalVariacoes} variações`}
                                </span>
                              </div>
                            )}
                            {emOutra ? (
                              <Button
                                className="botao-disabled fonteTexto"
                                disabled={true}
                                title=""
                              />
                            ) : naPromocao ? (
                              <Button
                                className="botao-remover fonteTexto"
                                onClick={() => onRemover(produto.id)}
                                title=""
                              />
                            ) : (
                              <Button
                                className="botao-adicionar fonteTexto"
                                onClick={() =>
                                  bloqueadas.length
                                    ? setProdutoVariacoes(produto)
                                    : onAdicionar(produto.id)
                                }
                                title=""
                              />
                            )}
                          </div>
                        </Produto>
                      </div>
                    );
                  })}
                </>
              </BackendInfiniteScroll>
            )}
            {!loading && !loadingMore && produtosFiltrados.length === 0 && (
              <div className="mensagem">
                <div className="texto-titulo fonteSubtitulo">
                  Nenhum resultado encontrado para sua busca.
                </div>
                <div className="texto-subtitulo fonteRotulos">
                  Tente novamente com uma busca diferente!
                </div>
              </div>
            )}
          </div>
        </div>
      </Dropdown>

      <ModalVariacoesPromocao
        isOpen={produtoVariacoes !== null}
        setModal={(aberto) => {
          if (!aberto) setProdutoVariacoes(null);
        }}
        idProduto={produtoVariacoes?.id || 0}
        nomeProduto={produtoVariacoes?.referencia || produtoVariacoes?.nome}
        idTabela={catalogo?.id_tabela}
        moeda={moeda}
        selecionadas={
          produtoVariacoes ? variacoesPorProduto[produtoVariacoes.id] || [] : []
        }
        bloqueadas={
          produtoVariacoes ? variacoesEmOutraPromocao[produtoVariacoes.id] || [] : []
        }
        onAplicar={(variacoes) => {
          if (!produtoVariacoes) return;
          onAlterarVariacoes(produtoVariacoes.id, variacoes);
          if (!produtosPromocaoAtual.includes(produtoVariacoes.id)) {
            onAdicionar(produtoVariacoes.id);
          }
          setProdutoVariacoes(null);
        }}
      />
    </ProdutosPromocaoContainer>
  );
};

export default ProdutosPromocao;
